import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('❌ Supabase Configuration Error:');
  console.error('VITE_SUPABASE_URL:', supabaseUrl ? '✅ Set' : '❌ Missing');
  console.error('VITE_SUPABASE_ANON_KEY:', supabaseAnonKey ? '✅ Set' : '❌ Missing');
  throw new Error('Missing Supabase environment variables. Please check your .env file for VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY');
}

console.log('✅ Supabase client initializing with URL:', supabaseUrl);

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
  }
});

// Connection status tracking
export const connectionStatus = {
  isOnline: navigator.onLine,
  supabaseReachable: null,
  lastChecked: null,
  projectPaused: false
};

// Network connectivity listeners
window.addEventListener('online', () => {
  connectionStatus.isOnline = true;
  console.log('🌐 Network connection restored');
  testSupabaseConnection();
});

window.addEventListener('offline', () => {
  connectionStatus.isOnline = false;
  console.warn('⚠️ Network connection lost');
});

// Test Supabase connection
export async function testSupabaseConnection() {
  if (!connectionStatus?.isOnline) {
    connectionStatus.supabaseReachable = false;
    return { success: false, error: 'No internet connection' };
  }

  try {
    // Use a simple query that doesn't depend on schema cache
    const { error } = await supabase?.from('products')?.select('count', { count: 'exact', head: true })?.limit(0);
    
    connectionStatus.lastChecked = new Date();
    
    if (error) {
      connectionStatus.supabaseReachable = false;
      
      if (error?.message?.includes('Failed to fetch')) {
        connectionStatus.projectPaused = true;
        console.error('❌ Supabase Connection Failed: Unable to reach Supabase server');
        console.error('Please check:');
        console.error('1. Is your Supabase project active? (may be paused)');
        console.error('2. Are the credentials correct?');
        console.error('3. Visit: https://supabase.com/dashboard/project/' + supabaseUrl?.split('//')?.[1]?.split('.')?.[0]);
        return { success: false, error: 'Supabase project may be paused or unreachable', projectPaused: true };
      }
      
      console.error('❌ Supabase connection test failed:', error?.message);
      return { success: false, error: error?.message };
    }
    
    connectionStatus.supabaseReachable = true;
    connectionStatus.projectPaused = false;
    console.log('✅ Supabase connection verified');
    return { success: true };
  } catch (err) {
    connectionStatus.supabaseReachable = false;
    console.error('❌ Supabase connection test error:', err?.message);
    return { success: false, error: err?.message };
  }
}

// Helper to check if operations should proceed
export function canProceedWithOperation() {
  if (!connectionStatus?.isOnline) {
    throw new Error('Sem conexão com a internet. Verifique sua rede e tente novamente.');
  }
  
  if (connectionStatus?.supabaseReachable === false) {
    if (connectionStatus?.projectPaused) {
      throw new Error('Projeto Supabase pausado ou inacessível. Visite o painel do Supabase para reativar o projeto.');
    }
    throw new Error('Não foi possível conectar ao servidor. Verifique sua conexão e tente novamente.');
  }
}

// Test connection on initialization (non-blocking)
testSupabaseConnection();
